// src/components/admin/blog/PostsManagement.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2,
  FileText,
  Calendar,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import StatusBadge from '../shared/StatusBadge';

const PostsManagement = ({ showNotification }) => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  
  // Filtering and pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [sortOption, setSortOption] = useState('newest');
  
  const navigate = useNavigate();
  const location = useLocation();

  const [showMobileFilters, setShowMobileFilters] = useState(false);

  
  useEffect(() => {
    // Parse query parameters from URL
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    const categoryParam = params.get('category');
    const tagParam = params.get('tag');
    const searchParam = params.get('search');
    const sortParam = params.get('sort');
    const pageParam = parseInt(params.get('page')) || 1;
    const limitParam = parseInt(params.get('limit')) || 10;
    
    if (statusParam) setStatusFilter(statusParam);
    if (categoryParam) setCategoryFilter(categoryParam);
    if (tagParam) setTagFilter(tagParam);
    if (searchParam) setSearchTerm(searchParam);
    if (sortParam) setSortOption(sortParam);
    setPage(pageParam);
    setLimit(limitParam);
    
    // Fetch filter options (categories and tags)
    fetchFilterOptions();
    
    // Fetch posts based on URL parameters
    fetchPosts(pageParam, limitParam, searchParam, statusParam, categoryParam, tagParam, sortParam);
  }, [location.search]);
  
  const fetchFilterOptions = async () => {
    try {
      const [categoriesRes, tagsRes] = await Promise.all([
        fetch('/api/blog/categories'),
        fetch('/api/blog/tags')
      ]);
      
      if (!categoriesRes.ok) throw new Error('Failed to fetch categories');
      if (!tagsRes.ok) throw new Error('Failed to fetch tags');
      
      const categoriesData = await categoriesRes.json();
      const tagsData = await tagsRes.json();
      
      setCategories(categoriesData);
      setTags(tagsData);
    } catch (error) {
      console.error('Error fetching filter options:', error);
      showNotification('error', 'Failed to load filter options');
    }
  };
  
  const fetchPosts = async (
    page = 1,
    limit = 10,
    search = '',
    status = 'all',
    category = '',
    tag = '',
    sort = 'newest'
  ) => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        page,
        limit,
        sort
      });
      
      if (search) params.append('search', search);
      if (status !== 'all') params.append('status', status);
      if (category) params.append('category', category);
      if (tag) params.append('tag', tag);
      
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/blog/posts?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to fetch posts');
      
      const data = await res.json();
      setPosts(data.posts);
      setTotalPages(data.totalPages);
      setTotalPosts(data.total);
      
    } catch (error) {
      console.error('Error fetching posts:', error);
      showNotification('error', 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to delete post');
      }
      
      // Refresh the posts list
      fetchPosts(page, limit, searchTerm, statusFilter, categoryFilter, tagFilter, sortOption);
      showNotification('success', 'Post deleted successfully');
    } catch (error) {
      console.error('Error deleting post:', error);
      showNotification('error', error.message || 'Failed to delete post');
    }
  };
  
  const applyFilters = () => {
    // Build URL with filters
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    if (categoryFilter) params.append('category', categoryFilter);
    if (tagFilter) params.append('tag', tagFilter);
    if (sortOption !== 'newest') params.append('sort', sortOption);
    if (page !== 1) params.append('page', page);
    if (limit !== 10) params.append('limit', limit);
    
    navigate(`/admin/blog/posts?${params.toString()}`);
  };
  
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCategoryFilter('');
    setTagFilter('');
    setSortOption('newest');
    setPage(1);
    
    navigate('/admin/blog/posts');
  };
  
  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(location.search);
    params.set('page', newPage);
    navigate(`/admin/blog/posts?${params.toString()}`);
  };
  
  // Function to format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  return (
    <div className="min-h-screen  bg-white rounded-lg shadow-md py-2 px-2">
      <div className="pr-6 py-4 border-b border-gray-200 flex justify-between items-center">    
        <h2 className="text-xl font-semibold">
          <button 
            onClick={() => navigate('/admin/blog')}
            style={{ 
              boxShadow: 'none', 
              border: 'none', 
              transform: 'none',
            }}
            className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft size={24}/>
            </button>
            <span>Posts Management</span>
        </h2>
        <button
          onClick={() => navigate('/admin/blog/posts/new')}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Post
        </button>
      </div>
      
      {/* Filters */}
      <div className="border-b border-gray-200 p-4 bg-gray-50">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search posts..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="w-full md:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 pr-8 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          
          <div className="w-full md:w-auto">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-8 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category._id} value={category.slug}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="w-full md:w-auto">
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-8 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Tags</option>
              {tags.map(tag => (
                <option key={tag._id} value={tag.slug}>
                  {tag.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="w-full md:w-auto">
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-8 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">Title (A-Z)</option>
              <option value="popular">Most Viewed</option>
            </select>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={applyFilters}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </button>
            
            <button
              onClick={resetFilters}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </button>
          </div>
        </div>
      </div>
      
      {/* Posts List */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-500">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            
            <h3 className="text-lg font-medium text-gray-900">No posts found</h3>
            <p className="mt-1 text-gray-500">Get started by creating a new post</p>
            <button
              onClick={() => navigate('/admin/blog/posts/new')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Post
            </button>
          </div>
        ) : (
          <div className="responsive-table">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Views
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.map(post => (
                <tr key={post._id} className="hover:bg-gray-50">
                  <td data-label="Title" className="px-6 py-4">
                    <div className="flex items-center">
                      {post.coverImage ? (
                        <img 
                          src={post.coverImage} 
                          alt={post.title} 
                          className="h-10 w-10 object-cover rounded-md mr-3"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-md bg-gray-200 flex items-center justify-center mr-3">
                          <FileText className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div className="truncate max-w-xs">
                        <div className="text-sm font-medium text-gray-900">{post.title}</div>
                        {post.excerpt && (
                          <div className="text-xs text-gray-500 truncate">{post.excerpt}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td data-label="Category" className="px-6 py-4">
                    {post.category && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {post.category.name}
                      </span>
                    )}
                  </td>
                  <td data-label="Status" className="px-6 py-4 text-center">
                    <StatusBadge status={post.status} />
                  </td>
                  <td data-label="Views" className="px-6 py-4 text-center text-sm text-gray-500">
                    {post.viewCount}
                  </td>
                  <td data-label="Date" className="px-6 py-4 text-center text-sm text-gray-500">
                    <div className="flex flex-col">
                      <span>{formatDate(post.createdAt)}</span>
                      {post.publishedAt && (
                        <span className="text-xs text-gray-400">
                          {post.status === 'published' ? 'Published: ' : ''}
                          {formatDate(post.publishedAt)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td data-label="Actions" className="px-6 py-4 text-center text-sm font-medium actions-mobile">
                    <div className="flex items-center justify-center space-x-2">
                      <button 
                        onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                        className="text-blue-600 hover:text-blue-900"
                        title="View"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => navigate(`/admin/blog/posts/edit/${post._id}`)}
                        className="text-amber-600 hover:text-amber-900"
                        title="Edit"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => handleDeletePost(post._id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
      
      {/* Pagination */}
      {!loading && posts.length > 0 && (
        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                page === 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                page === totalPages
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{posts.length}</span> results of{' '}
                <span className="font-medium">{totalPosts}</span> total
              </p>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <div>
                  <select
                    value={limit}
                    onChange={(e) => {
                      const params = new URLSearchParams(location.search);
                      params.set('limit', e.target.value);
                      params.set('page', '1'); // Reset to page 1 when changing limit
                      navigate(`/admin/blog/posts?${params.toString()}`);
                    }}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-8 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="10">10 per page</option>
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                  </select>
                </div>
                
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                      page === 1
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {/* Display page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={i}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border ${
                          page === pageNum
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        } text-sm font-medium`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                      page === totalPages
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
      <style >{`
        button {
          box-shadow: none !important;
          border: none !important;
          transform: none !important;
        }
          /* Add this CSS to your component's style section or to your global CSS */

        /* Mobile-specific table transformation */
        @media screen and (max-width: 767px) {
          /* Make the responsive-table container full width but without overflow */
          .responsive-table {
            width: 100%;
            overflow-x: visible;
            -webkit-overflow-scrolling: touch;
          }
          
          /* Transform the table structure for mobile */
          .responsive-table table,
          .responsive-table thead,
          .responsive-table tbody,
          .responsive-table th,
          .responsive-table td,
          .responsive-table tr {
            display: block;
            width: 100%;
          }
          
          /* Hide table headers visually but keep them accessible for screen readers */
          .responsive-table thead tr {
            position: absolute;
            top: -9999px;
            left: -9999px;
          }
          
          /* Style each row as a card */
          .responsive-table tbody tr {
            border: 1px solid #eaeaea;
            border-radius: 8px;
            margin-bottom: 16px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            background-color: white;
          }
          
          /* Reset any fixed widths or min-widths that might be causing overflow */
          .responsive-table table {
            min-width: 0 !important;
            width: 100% !important;
          }
          
          /* Style each cell as a row with label-value pair */
          .responsive-table td {
            position: relative;
            padding: 12px 12px 12px 45% !important; /* Important to override any inline styles */
            text-align: right !important;
            min-height: 30px;
            border-bottom: 1px solid #f0f0f0;
          }
          
          /* Create the label using data-label attribute */
          .responsive-table td:before {
            content: attr(data-label);
            position: absolute;
            top: 12px;
            left: 12px;
            width: 40%;
            padding-right: 10px;
            white-space: nowrap;
            font-weight: 600;
            text-align: left;
            color: #6b7280;
          }
          
          /* Remove bottom border from last cell */
          .responsive-table td:last-child {
            border-bottom: none;
          }
          
          /* Special styling for actions column */
          .responsive-table td.actions-mobile {
            padding: 12px !important;
            text-align: center !important;
          }
          
          /* Adjust title cell content */
          .responsive-table td[data-label="Title"] .flex {
            flex-direction: row;
            justify-content: flex-end;
            align-items: center;
          }
          
          /* Give more space for the title text */
          .responsive-table td[data-label="Title"] .truncate {
            max-width: 100%;
            text-align: right;
          }
          
          /* Ensure all content is visible */
          .responsive-table .truncate {
            white-space: normal;
            overflow: visible;
            text-overflow: clip;
          }
        }

        /* Optional: Add smooth transition for better UX */
        .responsive-table tr,
        .responsive-table td {
          transition: all 0.3s ease;
        }
       `}</style>
    </div>
  );
};

export default PostsManagement;




// // src/components/admin/blog/PostsManagement.js
// import React, { useState, useEffect } from 'react';
// import { useNavigate, useLocation } from 'react-router-dom';
// import { 
//   Plus, 
//   Search, 
//   Filter, 
//   Eye, 
//   Edit, 
//   Trash2,
//   FileText,
//   Calendar,
//   RefreshCw,
//   ChevronLeft,
//   ChevronRight,
//   ArrowLeft,
// } from 'lucide-react';
// import StatusBadge from '../shared/StatusBadge';

// const PostsManagement = ({ showNotification }) => {
//   const [posts, setPosts] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [tags, setTags] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [totalPages, setTotalPages] = useState(1);
//   const [totalPosts, setTotalPosts] = useState(0);
  
//   // Filtering and pagination states
//   const [page, setPage] = useState(1);
//   const [limit, setLimit] = useState(10);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [statusFilter, setStatusFilter] = useState('all');
//   const [categoryFilter, setCategoryFilter] = useState('');
//   const [tagFilter, setTagFilter] = useState('');
//   const [sortOption, setSortOption] = useState('newest');
  
//   const navigate = useNavigate();
//   const location = useLocation();

//   const [showMobileFilters, setShowMobileFilters] = useState(false);

  
//   useEffect(() => {
//     // Parse query parameters from URL
//     const params = new URLSearchParams(location.search);
//     const statusParam = params.get('status');
//     const categoryParam = params.get('category');
//     const tagParam = params.get('tag');
//     const searchParam = params.get('search');
//     const sortParam = params.get('sort');
//     const pageParam = parseInt(params.get('page')) || 1;
//     const limitParam = parseInt(params.get('limit')) || 10;
    
//     if (statusParam) setStatusFilter(statusParam);
//     if (categoryParam) setCategoryFilter(categoryParam);
//     if (tagParam) setTagFilter(tagParam);
//     if (searchParam) setSearchTerm(searchParam);
//     if (sortParam) setSortOption(sortParam);
//     setPage(pageParam);
//     setLimit(limitParam);
    
//     // Fetch filter options (categories and tags)
//     fetchFilterOptions();
    
//     // Fetch posts based on URL parameters
//     fetchPosts(pageParam, limitParam, searchParam, statusParam, categoryParam, tagParam, sortParam);
//   }, [location.search]);
  
//   const fetchFilterOptions = async () => {
//     try {
//       const [categoriesRes, tagsRes] = await Promise.all([
//         fetch('/api/blog/categories'),
//         fetch('/api/blog/tags')
//       ]);
      
//       if (!categoriesRes.ok) throw new Error('Failed to fetch categories');
//       if (!tagsRes.ok) throw new Error('Failed to fetch tags');
      
//       const categoriesData = await categoriesRes.json();
//       const tagsData = await tagsRes.json();
      
//       setCategories(categoriesData);
//       setTags(tagsData);
//     } catch (error) {
//       console.error('Error fetching filter options:', error);
//       showNotification('error', 'Failed to load filter options');
//     }
//   };
  
//   const fetchPosts = async (
//     page = 1,
//     limit = 10,
//     search = '',
//     status = 'all',
//     category = '',
//     tag = '',
//     sort = 'newest'
//   ) => {
//     setLoading(true);
//     try {
//       // Build query params
//       const params = new URLSearchParams({
//         page,
//         limit,
//         sort
//       });
      
//       if (search) params.append('search', search);
//       if (status !== 'all') params.append('status', status);
//       if (category) params.append('category', category);
//       if (tag) params.append('tag', tag);
      
//       const token = localStorage.getItem('token');
//       const res = await fetch(`/api/blog/posts?${params.toString()}`, {
//         headers: {
//           Authorization: `Bearer ${token}`
//         }
//       });
      
//       if (!res.ok) throw new Error('Failed to fetch posts');
      
//       const data = await res.json();
//       setPosts(data.posts);
//       setTotalPages(data.totalPages);
//       setTotalPosts(data.total);
      
//     } catch (error) {
//       console.error('Error fetching posts:', error);
//       showNotification('error', 'Failed to load posts');
//     } finally {
//       setLoading(false);
//     }
//   };
  
//   const handleDeletePost = async (postId) => {
//     if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
//       return;
//     }
    
//     try {
//       const token = localStorage.getItem('token');
//       const res = await fetch(`/api/blog/posts/${postId}`, {
//         method: 'DELETE',
//         headers: {
//           Authorization: `Bearer ${token}`
//         }
//       });
      
//       if (!res.ok) {
//         const errorData = await res.json();
//         throw new Error(errorData.error || 'Failed to delete post');
//       }
      
//       // Refresh the posts list
//       fetchPosts(page, limit, searchTerm, statusFilter, categoryFilter, tagFilter, sortOption);
//       showNotification('success', 'Post deleted successfully');
//     } catch (error) {
//       console.error('Error deleting post:', error);
//       showNotification('error', error.message || 'Failed to delete post');
//     }
//   };
  
//   const applyFilters = () => {
//     // Build URL with filters
//     const params = new URLSearchParams();
//     if (searchTerm) params.append('search', searchTerm);
//     if (statusFilter !== 'all') params.append('status', statusFilter);
//     if (categoryFilter) params.append('category', categoryFilter);
//     if (tagFilter) params.append('tag', tagFilter);
//     if (sortOption !== 'newest') params.append('sort', sortOption);
//     if (page !== 1) params.append('page', page);
//     if (limit !== 10) params.append('limit', limit);
    
//     navigate(`/admin/blog/posts?${params.toString()}`);
//   };
  
//   const resetFilters = () => {
//     setSearchTerm('');
//     setStatusFilter('all');
//     setCategoryFilter('');
//     setTagFilter('');
//     setSortOption('newest');
//     setPage(1);
    
//     navigate('/admin/blog/posts');
//   };
  
//   const handlePageChange = (newPage) => {
//     const params = new URLSearchParams(location.search);
//     params.set('page', newPage);
//     navigate(`/admin/blog/posts?${params.toString()}`);
//   };
  
//   // Function to format date
//   const formatDate = (dateString) => {
//     const options = { year: 'numeric', month: 'short', day: 'numeric' };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   };
  
//   return (
//     <div className="min-h-screen  bg-white rounded-lg shadow-md py-2 px-2">
//       <div className="pr-6 py-4 border-b border-gray-200 flex justify-between items-center">    
//         <h2 className="text-xl font-semibold">
//           <button 
//             onClick={() => navigate('/admin/blog')}
//             style={{ 
//               boxShadow: 'none', 
//               border: 'none', 
//               transform: 'none',
//             }}
//             className="text-gray-600 hover:text-gray-900"
//             >
//               <ArrowLeft size={24}/>
//             </button>
//             <span>Posts Management</span>
//         </h2>
//         <button
//           onClick={() => navigate('/admin/blog/posts/new')}
//           className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
//         >
//           <Plus className="h-4 w-4 mr-2" />
//           New Post
//         </button>
//       </div>
      
//       {/* Filters */}
//       <div className="border-b border-gray-200 p-4 bg-gray-50">
//         <div className="flex flex-wrap gap-3">
//           <div className="flex-1 min-w-[200px]">
//             <div className="relative rounded-md shadow-sm">
//               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//                 <Search className="h-4 w-4 text-gray-400" />
//               </div>
//               <input
//                 type="text"
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 placeholder="Search posts..."
//                 className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
//               />
//             </div>
//           </div>
          
//           <div className="w-full md:w-auto">
//             <select
//               value={statusFilter}
//               onChange={(e) => setStatusFilter(e.target.value)}
//               className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 pr-8 focus:ring-blue-500 focus:border-blue-500"
//             >
//               <option value="all">All Status</option>
//               <option value="published">Published</option>
//               <option value="draft">Draft</option>
//             </select>
//           </div>
          
//           <div className="w-full md:w-auto">
//             <select
//               value={categoryFilter}
//               onChange={(e) => setCategoryFilter(e.target.value)}
//               className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-8 focus:ring-blue-500 focus:border-blue-500"
//             >
//               <option value="">All Categories</option>
//               {categories.map(category => (
//                 <option key={category._id} value={category.slug}>
//                   {category.name}
//                 </option>
//               ))}
//             </select>
//           </div>
          
//           <div className="w-full md:w-auto">
//             <select
//               value={tagFilter}
//               onChange={(e) => setTagFilter(e.target.value)}
//               className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-8 focus:ring-blue-500 focus:border-blue-500"
//             >
//               <option value="">All Tags</option>
//               {tags.map(tag => (
//                 <option key={tag._id} value={tag.slug}>
//                   {tag.name}
//                 </option>
//               ))}
//             </select>
//           </div>
          
//           <div className="w-full md:w-auto">
//             <select
//               value={sortOption}
//               onChange={(e) => setSortOption(e.target.value)}
//               className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-8 focus:ring-blue-500 focus:border-blue-500"
//             >
//               <option value="newest">Newest</option>
//               <option value="oldest">Oldest</option>
//               <option value="title">Title (A-Z)</option>
//               <option value="popular">Most Viewed</option>
//             </select>
//           </div>
          
//           <div className="flex space-x-2">
//             <button
//               onClick={applyFilters}
//               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
//             >
//               <Filter className="h-4 w-4 mr-2" />
//               Filter
//             </button>
            
//             <button
//               onClick={resetFilters}
//               className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 flex items-center"
//             >
//               <RefreshCw className="h-4 w-4 mr-2" />
//               Reset
//             </button>
//           </div>
//         </div>
//       </div>
      
//       {/* Posts List */}
//       <div className="overflow-x-auto">
//         {loading ? (
//           <div className="text-center py-12">
//             <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
//             <p className="mt-2 text-gray-500">Loading posts...</p>
//           </div>
//         ) : posts.length === 0 ? (
//           <div className="text-center py-12">
//             <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            
//             <h3 className="text-lg font-medium text-gray-900">No posts found</h3>
//             <p className="mt-1 text-gray-500">Get started by creating a new post</p>
//             <button
//               onClick={() => navigate('/admin/blog/posts/new')}
//               className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//             >
//               <Plus className="h-4 w-4 mr-2" />
//               New Post
//             </button>
//           </div>
//         ) : (
//           <table className="min-w-full divide-y divide-gray-200">
//             <thead className="bg-gray-50">
//               <tr>
//                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Title
//                 </th>
//                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Category
//                 </th>
//                 <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Status
//                 </th>
//                 <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Views
//                 </th>
//                 <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Date
//                 </th>
//                 <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   Actions
//                 </th>
//               </tr>
//             </thead>
//             <tbody className="bg-white divide-y divide-gray-200">
//               {posts.map(post => (
//                 <tr key={post._id} className="hover:bg-gray-50">
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     <div className="flex items-center">
//                       {post.coverImage ? (
//                         <img 
//                           src={post.coverImage} 
//                           alt={post.title} 
//                           className="h-10 w-10 object-cover rounded-md mr-3"
//                         />
//                       ) : (
//                         <div className="h-10 w-10 rounded-md bg-gray-200 flex items-center justify-center mr-3">
//                           <FileText className="h-5 w-5 text-gray-400" />
//                         </div>
//                       )}
//                       <div className="truncate max-w-xs">
//                         <div className="text-sm font-medium text-gray-900">{post.title}</div>
//                         {post.excerpt && (
//                           <div className="text-xs text-gray-500 truncate">{post.excerpt}</div>
//                         )}
//                       </div>
//                     </div>
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     {post.category && (
//                       <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
//                         {post.category.name}
//                       </span>
//                     )}
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap text-center">
//                     <StatusBadge status={post.status} />
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
//                     {post.viewCount}
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
//                     <div className="flex flex-col">
//                       <span>{formatDate(post.createdAt)}</span>
//                       {post.publishedAt && (
//                         <span className="text-xs text-gray-400">
//                           {post.status === 'published' ? 'Published: ' : ''}
//                           {formatDate(post.publishedAt)}
//                         </span>
//                       )}
//                     </div>
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
//                     <div className="flex items-center justify-center space-x-2">
//                       <button 
//                         onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
//                         className="text-blue-600 hover:text-blue-900"
//                         title="View"
//                       >
//                         <Eye className="h-5 w-5" />
//                       </button>
//                       <button 
//                         onClick={() => navigate(`/admin/blog/posts/edit/${post._id}`)}
//                         className="text-amber-600 hover:text-amber-900"
//                         title="Edit"
//                       >
//                         <Edit className="h-5 w-5" />
//                       </button>
//                       <button 
//                         onClick={() => handleDeletePost(post._id)}
//                         className="text-red-600 hover:text-red-900"
//                         title="Delete"
//                       >
//                         <Trash2 className="h-5 w-5" />
//                       </button>
//                     </div>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         )}
//       </div>
      
//       {/* Pagination */}
//       {!loading && posts.length > 0 && (
//         <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
//           <div className="flex-1 flex justify-between sm:hidden">
//             <button
//               onClick={() => handlePageChange(Math.max(1, page - 1))}
//               disabled={page === 1}
//               className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
//                 page === 1
//                   ? 'text-gray-300 cursor-not-allowed'
//                   : 'text-gray-700 hover:bg-gray-50'
//               }`}
//             >
//               Previous
//             </button>
//             <button
//               onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
//               disabled={page === totalPages}
//               className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
//                 page === totalPages
//                   ? 'text-gray-300 cursor-not-allowed'
//                   : 'text-gray-700 hover:bg-gray-50'
//               }`}
//             >
//               Next
//             </button>
//           </div>
//           <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
//             <div>
//               <p className="text-sm text-gray-700">
//                 Showing <span className="font-medium">{posts.length}</span> results of{' '}
//                 <span className="font-medium">{totalPosts}</span> total
//               </p>
//             </div>
//             <div>
//               <div className="flex items-center space-x-2">
//                 <div>
//                   <select
//                     value={limit}
//                     onChange={(e) => {
//                       const params = new URLSearchParams(location.search);
//                       params.set('limit', e.target.value);
//                       params.set('page', '1'); // Reset to page 1 when changing limit
//                       navigate(`/admin/blog/posts?${params.toString()}`);
//                     }}
//                     className="border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-8 focus:ring-blue-500 focus:border-blue-500"
//                   >
//                     <option value="10">10 per page</option>
//                     <option value="25">25 per page</option>
//                     <option value="50">50 per page</option>
//                     <option value="100">100 per page</option>
//                   </select>
//                 </div>
                
//                 <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
//                   <button
//                     onClick={() => handlePageChange(Math.max(1, page - 1))}
//                     disabled={page === 1}
//                     className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
//                       page === 1
//                         ? 'text-gray-300 cursor-not-allowed'
//                         : 'text-gray-500 hover:bg-gray-50'
//                     }`}
//                   >
//                     <ChevronLeft className="h-5 w-5" />
//                   </button>
                  
//                   {/* Display page numbers */}
//                   {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
//                     let pageNum;
//                     if (totalPages <= 5) {
//                       pageNum = i + 1;
//                     } else if (page <= 3) {
//                       pageNum = i + 1;
//                     } else if (page >= totalPages - 2) {
//                       pageNum = totalPages - 4 + i;
//                     } else {
//                       pageNum = page - 2 + i;
//                     }
                    
//                     return (
//                       <button
//                         key={i}
//                         onClick={() => handlePageChange(pageNum)}
//                         className={`relative inline-flex items-center px-4 py-2 border ${
//                           page === pageNum
//                             ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
//                             : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
//                         } text-sm font-medium`}
//                       >
//                         {pageNum}
//                       </button>
//                     );
//                   })}
                  
//                   <button
//                     onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
//                     disabled={page === totalPages}
//                     className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
//                       page === totalPages
//                         ? 'text-gray-300 cursor-not-allowed'
//                         : 'text-gray-500 hover:bg-gray-50'
//                     }`}
//                   >
//                     <ChevronRight className="h-5 w-5" />
//                   </button>
//                 </nav>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//       <style >{`
//         button {
//           box-shadow: none !important;
//           border: none !important;
//           transform: none !important;
//         }
//        `}</style>
//     </div>
//   );
// };

// export default PostsManagement;